import React, { useEffect, useState, useMemo, useCallback } from 'react';
import InsertAction from './InsertAction';
import UpdateConditionForm from '../components/UpdateConditionForm';
import ApiCaller from '../../../../api/ApiCaller';
import { DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET, PARTICULAR_REQUEST_PARAMETER_LIST_GET } from '../../../../api/ApiConst';

const UpdateAction = ({ 
    formData, 
    setFormData, 
    allTableList, 
    selectedRequest, 
    allPreviousSteps, 
    currentStepId 
}) => {
    const [tableFieldOptions, setTableFieldOptions] = useState([]);
    const [valueParameters, setValueParameters] = useState([]);
    const [parametersLoading, setParametersLoading] = useState(false);

    // Memoize tableId to prevent unnecessary effect re-runs
    const tableId = useMemo(() => formData?.tableId, [formData?.tableId]);

    // Load selected table's fields for the condition field dropdown
    useEffect(() => {
        if (!tableId) {
            setTableFieldOptions([]);
            return;
        }

        const requestBody = {
            page: 0,
            size: 1000,
            searchFilters: [],
            sortRules: [],
        };
        const apiUrl = DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET.replaceAll('{dynamic-table-id}', tableId);

        ApiCaller.callPostApi(
            apiUrl,
            requestBody,
            {},
            (response) => {
                const attrs = Array.isArray(response?.data) ? response.data : [];
                // Map to Select options; use attribute name as value for update conditions
                const options = attrs.map(a => ({ value: a.name, label: a.name }));
                setTableFieldOptions(options);
            },
            () => setTableFieldOptions([])
        );
    }, [tableId]);

    // Memoize sources to prevent unnecessary effect re-runs
    const requestParamSources = useMemo(() => 
        Array.isArray(formData?.requestParamSource) ? formData.requestParamSource : [], 
        [formData?.requestParamSource]
    );

    // Build value parameters list from selected requestParamSource
    useEffect(() => {
        if (requestParamSources.length === 0) {
            setValueParameters([]);
            return;
        }

        let combined = [];
        setParametersLoading(true);

        const addCustomRequestParams = () => new Promise((resolve) => {
            const requestId = selectedRequest;
            if (!requestId) return resolve();

            const apiUrl = PARTICULAR_REQUEST_PARAMETER_LIST_GET.replaceAll('{request-id}', requestId);
            ApiCaller.callPostApi(
                apiUrl,
                null,
                {},
                (response) => {
                    const data = Array.isArray(response?.data) ? response.data : [];
                    const opts = data.map(p => ({
                        value: `custom_request.${p.name}`,
                        label: `${p.name} (Request)`,
                        type: p.type,
                        attributes: Array.isArray(p?.config?.attributes) ? p.config.attributes.map(a => ({ name: a.name, type: a.type })) : []
                    }));
                    combined = combined.concat(opts);
                    resolve();
                },
                () => resolve()
            );
        });

        const addStepParams = () => {
            const stepOpts = [];
            requestParamSources.forEach(source => {
                if (source !== 'custom_request') {
                    const stepId = source.toString().startsWith('step_') ? source.replace('step_', '') : source;
                    const step = allPreviousSteps?.find(s => s.id === parseInt(stepId));
                    const params = step?.actions?.[0]?.config?.responseParameters || step?.action?.config?.responseParameters || [];
                    params.forEach(param => {
                        if (!param) return;
                        const name = param.name || param.id || '';
                        if (!name) return;
                        stepOpts.push({
                            value: `step_${stepId}.${name}`,
                            label: `${name} (Step ${stepId})`,
                            type: param.type,
                            attributes: Array.isArray(param?.config?.attributes) ? param.config.attributes.map(a => ({ name: a.name, type: a.type })) : []
                        });
                    });
                }
            });
            combined = combined.concat(stepOpts);
        };

        (async () => {
            if (requestParamSources.includes('custom_request')) {
                await addCustomRequestParams();
            }
            addStepParams();
            setValueParameters(combined);
            setParametersLoading(false);
        })();

    }, [requestParamSources, selectedRequest, allPreviousSteps]);

    // Memoize props to prevent unnecessary re-renders of UpdateConditionForm
    const conditionFormProps = useMemo(() => ({
        formData,
        setFormData,
        requestParameters: tableFieldOptions,
        valueParameters,
        parametersLoading
    }), [formData, setFormData, tableFieldOptions, valueParameters, parametersLoading]);

    return (
        <>
            <InsertAction
                formData={formData}
                setFormData={setFormData}
                allTableList={allTableList}
                selectedRequest={selectedRequest}
                allPreviousSteps={allPreviousSteps}
                currentStepId={currentStepId}
            />
            <hr style={{ margin: "20px 0 30px" }} />
            <UpdateConditionForm {...conditionFormProps} />
        </>
    );
};

export default UpdateAction; 